/**
 * The north-up fallback (`ProductOverview.md` §4.7).
 *
 * When there is no compass — permission denied, sensor absent, desktop browser — an arrow that
 * cannot rotate with you would be a lie. This replaces it with a static compass face, north at
 * the top, the target's direction marked on it, and the same direction written out in words.
 * It is genuinely usable: hold the phone flat, face north, and walk the way the mark says.
 *
 * Plain SVG, because there is nothing here that needs a GPU.
 */

import { compassPoint, normalizeDegrees } from '@beeline/shared';

const SVG_NS = 'http://www.w3.org/2000/svg';
const SIZE = 240;
const CENTRE = SIZE / 2;

export interface NorthUpView {
  element: HTMLElement;
  update: (bearing: number | null) => void;
  destroy: () => void;
}

function svg<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attributes: Record<string, string | number>,
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [key, value] of Object.entries(attributes)) node.setAttribute(key, String(value));
  return node;
}

/** A point on the compass face, from a bearing in degrees clockwise from north. */
function pointAt(bearing: number, radius: number): { x: number; y: number } {
  const radians = (bearing - 90) * (Math.PI / 180);
  return { x: CENTRE + Math.cos(radians) * radius, y: CENTRE + Math.sin(radians) * radius };
}

export function createNorthUpView(): NorthUpView {
  const face = svg('svg', {
    viewBox: `0 0 ${SIZE} ${SIZE}`,
    width: SIZE,
    height: SIZE,
    class: 'northup__face',
    role: 'img',
    'aria-label': 'Compass, north at the top',
  });

  face.append(
    svg('circle', {
      cx: CENTRE,
      cy: CENTRE,
      r: 104,
      fill: 'none',
      stroke: '#23303F',
      'stroke-width': 2,
    }),
  );

  // Cardinal labels. North is amber, because "north is up" is the whole contract of this view.
  const cardinals: Array<[string, number]> = [
    ['N', 0],
    ['E', 90],
    ['S', 180],
    ['W', 270],
  ];
  for (const [label, bearing] of cardinals) {
    const at = pointAt(bearing, 84);
    const text = svg('text', {
      x: at.x,
      y: at.y,
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
      class: bearing === 0 ? 'northup__cardinal northup__cardinal--north' : 'northup__cardinal',
    });
    text.textContent = label;
    face.append(text);
  }

  // The marker that shows where the peer is. Hidden until there is a bearing to show.
  const needle = svg('line', {
    x1: CENTRE,
    y1: CENTRE,
    x2: CENTRE,
    y2: CENTRE - 96,
    class: 'northup__needle',
    'stroke-linecap': 'round',
  });
  const head = svg('circle', { cx: CENTRE, cy: CENTRE - 96, r: 7, class: 'northup__head' });
  const hub = svg('circle', { cx: CENTRE, cy: CENTRE, r: 4, class: 'northup__hub' });
  face.append(needle, head, hub);

  const words = document.createElement('p');
  words.className = 'northup__words';

  const element = document.createElement('div');
  element.className = 'northup';
  element.append(face, words);

  const update = (bearing: number | null): void => {
    const known = bearing !== null && Number.isFinite(bearing);
    needle.style.opacity = known ? '1' : '0';
    head.style.opacity = known ? '1' : '0';

    if (!known) {
      words.textContent = 'No direction yet';
      return;
    }

    const at = pointAt(normalizeDegrees(bearing), 96);
    needle.setAttribute('x2', String(at.x));
    needle.setAttribute('y2', String(at.y));
    head.setAttribute('cx', String(at.x));
    head.setAttribute('cy', String(at.y));

    words.textContent = `${compassPoint(bearing)} of you`;
  };

  update(null);

  return { element, update, destroy: () => element.remove() };
}
